/*LIBRARY*/
import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { connect } from "react-redux";

/*COMPONENT*/
import SIDEBAR_CONTENT from "./SIDEBAR_CONTENT";
import SIDEBAR_MINI from "./SIDEBAR_MINI";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";
import { set_value_layer } from "../../App/actions/layerActions";
import { set_value_insight } from "../../App/actions/insight_v2";
import TOOLBOX_COMPANION from "../toolbox/TOOLBOX_COMPANION";
import {
  set_value_toolbox,
  toolbox_handle_back,
  toolbox_handle_cancel,
  toolbox_handle_stop,
} from "../../App/actions/toolboxActions";
import Modal from "../common_modal/Modal";
import useLanguage from "../common_hooks/useLanguage";
import TOOLBOX_SETTING from "../toolbox/TOOLBOX_SETTING";

/*CONST*/
const MIN_HEIGHT = 185;
const MAX_HEIGHT = window.innerHeight * 0.97;
const MOBILE_WIDTH = 767;
const SIDEBAR_CLOSED = 70;

const RIGHT_CONTAINER = ({
  layer,
  properties,
  set_value_properties,
  set_value_layer,
  set_value_insight,
  toolbox,
  toolbox_handle_cancel,
  set_value_toolbox,
  toolbox_handle_stop,
  toolbox_handle_back,
}) => {
  const {
    sidebar_right_status,
    sidebar_height,
    is_mobile,
    is_dragging,
    is_mobile_close,
  } = properties;
  const { geo_layer_list, layer_id } = layer;
  const { modal_cancel_confirmation, name_toolbox } = toolbox || {};

  const [is_transitioning_open, set_is_transitioning_open] = useState(false);

  const prev_layer_id_ref = useRef();
  const drag_timeout_ref = useRef(null);
  const drag_handle_ref = useRef(null);
  const drag_info_ref = useRef({
    initial_y: 0,
    initial_x: 0,
    initial_height: 0,
    last_y: 0,
    last_time: 0,
  });

  const { dict, language } = useLanguage();

  // ─── Helper: toggle MapLibre canvas pointer events ────────────────────────────

  const set_map_canvas_pointer_events = useCallback((value) => {
    const map_canvas = document.querySelector(".maplibregl-canvas");
    if (map_canvas) map_canvas.style.pointerEvents = value;
  }, []);

  // ─── Stable setters ──────────────────────────────────────────────────────────

  const set_is_dragging = useCallback(
    (value) => set_value_properties({ key: "is_dragging", value }),
    [set_value_properties],
  );

  const set_is_mobile = useCallback(
    (value) => {
      set_value_properties({ key: "is_mobile", value });
      if (value === false) {
        set_value_properties({ key: "is_mobile_close", value: false });
        set_value_properties({ key: "is_transitioning_open", value: false });
        set_value_properties({ key: "sidebar_height", value: "pc" });
      }
    },
    [set_value_properties],
  );

  const set_modal_cancel_confirmation = useCallback(
    (value) => {
      set_value_toolbox({
        key: "modal_cancel_confirmation",
        value: { is_open: value, is_back_active: false, tab_value: "" },
      });
    },
    [set_value_toolbox],
  );

  // ─── Layer ID effect ──────────────────────────────────────────────────────────

  useEffect(() => {
    const prev_layer_id = prev_layer_id_ref.current;
    prev_layer_id_ref.current = layer_id;

    if (!layer_id || prev_layer_id === layer_id) return;

    const geo_layer = geo_layer_list.find(
      (item) => item?.geo_layer?._id === layer_id,
    )?.geo_layer;

    const ss_version = geo_layer?.insight_object?.version;

    if (ss_version === 3) {
      set_value_properties({ key: "legend_status", value: false });
      set_value_layer({ key: "statistic_mode", value: "sini" });
      set_value_insight({ key: "site_selection_view", value: "report" });

      const insight_keys = [
        "array_formula_insight",
        "array_group_insight",
        "config_final_score",
        "provinsi",
        "kota",
        "kecamatan",
        "kelurahan",
        "tipe_1_array",
        "tipe_2_array",
        "tipe_3_array",
        "tipe_level",
        "tipe_1_all",
        "total_poi_initial",
        "grid_mode",
        "grid_counts",
        "grid_width_meters",
        "total_square_meters",
      ];
      insight_keys.forEach((key) =>
        set_value_insight({ key, value: geo_layer?.insight_object?.[key] }),
      );
    } else {
      set_value_layer({ key: "statistic_mode", value: "data" });
      set_value_insight({ key: "site_selection_view", value: "input" });
    }
  }, [
    layer_id,
    geo_layer_list,
    set_value_properties,
    set_value_layer,
    set_value_insight,
  ]);

  // ─── Resize listener ──────────────────────────────────────────────────────────

  useEffect(() => {
    const handle_resize = () =>
      set_is_mobile(window.innerWidth <= MOBILE_WIDTH);

    window.addEventListener("resize", handle_resize);
    return () => {
      window.removeEventListener("resize", handle_resize);
      if (drag_timeout_ref.current) clearTimeout(drag_timeout_ref.current);
    };
  }, [set_is_mobile]);

  // ─── Sidebar height logic ─────────────────────────────────────────────────────

  const set_sidebar_height = useCallback(
    (value) => {
      if (is_mobile_close) {
        set_is_transitioning_open(true);
        set_value_properties({ key: "sidebar_height", value: 500 });
        setTimeout(() => {
          set_value_properties({ key: "is_mobile_close", value: false });
          set_is_transitioning_open(false);
        }, 300);
      } else {
        set_value_properties({ key: "sidebar_height", value });
        if (value < MIN_HEIGHT + 10) {
          set_value_properties({
            key: "sidebar_height",
            value: SIDEBAR_CLOSED,
          });
          set_value_properties({ key: "is_mobile_close", value: true });
        }
      }
    },
    [is_mobile_close, set_value_properties],
  );

  const activate_bar = useCallback(() => {
    setTimeout(() => {
      set_value_properties({ key: "sidebar_right_status", value: true });
      set_value_properties({ key: "is_mobile_close", value: false });
      set_value_properties({ key: "sidebar_height", value: 500 });
    }, 10);
  }, [set_value_properties]);

  // ─── Drag helpers ─────────────────────────────────────────────────────────────

  const apply_drag_height = useCallback(
    (new_height, velocity = 0) => {
      if (new_height >= MIN_HEIGHT && new_height <= MAX_HEIGHT) {
        set_sidebar_height(new_height);
      }
      if (
        (velocity > 0.3 && new_height < MIN_HEIGHT + 60) ||
        new_height < MIN_HEIGHT + 10
      ) {
        if (drag_timeout_ref.current) clearTimeout(drag_timeout_ref.current);
        drag_timeout_ref.current = setTimeout(() => {
          set_value_properties({ key: "is_mobile_close", value: true });
          set_value_properties({
            key: "sidebar_height",
            value: SIDEBAR_CLOSED,
          });
        }, 50);
      }
    },
    [set_sidebar_height, set_value_properties],
  );

  const apply_drag_height_ref = useRef(apply_drag_height);
  useEffect(() => {
    apply_drag_height_ref.current = apply_drag_height;
  }, [apply_drag_height]);

  // ─── Mouse handlers (desktop) ─────────────────────────────────────────────────

  const handle_mouse_move = useCallback((e) => {
    const deltaY = e.clientY - drag_info_ref.current.initial_y;
    const new_height = drag_info_ref.current.initial_height - deltaY;
    apply_drag_height_ref.current(new_height);
  }, []);

  const handle_mouse_up = useCallback(() => {
    set_is_dragging(false);
    window.removeEventListener("mousemove", handle_mouse_move);
    window.removeEventListener("mouseup", handle_mouse_up);
  }, [set_is_dragging, handle_mouse_move]);

  const handle_mouse_down = useCallback(
    (e) => {
      e.preventDefault();
      set_is_dragging(true);
      drag_info_ref.current = {
        initial_y: e.clientY,
        initial_x: e.clientX,
        initial_height: sidebar_height,
        last_y: e.clientY,
        last_time: Date.now(),
      };
      window.addEventListener("mousemove", handle_mouse_move);
      window.addEventListener("mouseup", handle_mouse_up);
    },
    [set_is_dragging, sidebar_height, handle_mouse_move, handle_mouse_up],
  );

  // ─── Touch handlers (mobile) ──────────────────────────────────────────────────

  const handle_touch_move = useCallback((e) => {
    const current_y = e.touches[0].clientY;
    const current_x = e.touches[0].clientX;

    const deltaY = Math.abs(current_y - drag_info_ref.current.initial_y);
    const deltaX = Math.abs(current_x - drag_info_ref.current.initial_x);

    if (deltaX > deltaY) return;

    e.preventDefault();

    const now = Date.now();
    const time_diff = now - drag_info_ref.current.last_time;
    const y_diff = current_y - drag_info_ref.current.last_y;
    const velocity = y_diff / time_diff;

    drag_info_ref.current.last_y = current_y;
    drag_info_ref.current.last_time = now;

    const total_deltaY = current_y - drag_info_ref.current.initial_y;
    const new_height = drag_info_ref.current.initial_height - total_deltaY;

    apply_drag_height_ref.current(new_height, velocity);
  }, []);

  const handle_touch_end = useCallback(
    (e) => {
      set_is_dragging(false);
      set_map_canvas_pointer_events("auto");

      const handle_el = drag_handle_ref.current;
      if (handle_el) {
        handle_el.removeEventListener("touchmove", handle_touch_move);
        handle_el.removeEventListener("touchend", handle_touch_end);
      }
    },
    [set_is_dragging, handle_touch_move, set_map_canvas_pointer_events],
  );

  const handle_touch_end_ref = useRef(handle_touch_end);
  useEffect(() => {
    handle_touch_end_ref.current = handle_touch_end;
  }, [handle_touch_end]);

  const handle_touch_start = useCallback(
    (e) => {
      set_is_dragging(true);
      set_map_canvas_pointer_events("none");

      drag_info_ref.current = {
        initial_y: e.touches[0].clientY,
        initial_x: e.touches[0].clientX,
        initial_height: sidebar_height,
        last_y: e.touches[0].clientY,
        last_time: Date.now(),
      };

      const handle_el = e.currentTarget;
      drag_handle_ref.current = handle_el;

      handle_el.addEventListener("touchmove", handle_touch_move, {
        passive: false,
      });
      handle_el.addEventListener("touchend", handle_touch_end_ref.current);
    },
    [
      set_is_dragging,
      sidebar_height,
      handle_touch_move,
      set_map_canvas_pointer_events,
    ],
  );

  // ─── Cancel handler ───────────────────────────────────────────────────────────

  const handle_cancel = useCallback(() => {
    toolbox_handle_cancel();
    set_modal_cancel_confirmation(false);

    if (modal_cancel_confirmation?.is_back_active) {
      toolbox_handle_back();
      return;
    }
    if (modal_cancel_confirmation?.tab_value) {
      set_value_layer({
        key: "statistic_mode",
        value: modal_cancel_confirmation.tab_value,
      });
      return;
    }
    toolbox_handle_stop();
  }, [
    toolbox_handle_cancel,
    set_modal_cancel_confirmation,
    modal_cancel_confirmation,
    toolbox_handle_back,
    set_value_layer,
    toolbox_handle_stop,
  ]);

  // ─── Memoised styles ──────────────────────────────────────────────────────────

  const pc_sidebar_style = useMemo(
    () => ({
      position: "fixed",
      top: "53px",
      bottom: "37px",
      right: sidebar_right_status ? "7px" : "-352px",
      display: "flex",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "right 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)",
      gap: "30px",
    }),
    [sidebar_right_status],
  );

  const mobile_sidebar_style = useMemo(
    () => ({
      zIndex: 3,
      position: "fixed",
      bottom: "0",
      left: "0",
      // FIX: solid white instead of blur — backdrop-filter is expensive
      backgroundColor: "rgba(255, 255, 255, 1)",
      width: "100vw",
      height: `${sidebar_height}px`,
      borderRadius: "15px 15px 0 0",
      boxSizing: "border-box",
      overflow: "hidden",
      // FIX: simple border instead of boxShadow — blur/shadow repaints every drag frame
      borderTop: "1px solid rgba(0, 0, 0, 0.12)",
      display: "flex",
      flexDirection: "column",
      // FIX: only animate height, not "all" — reduces paint scope
      transition: is_dragging ? "none" : "height 0.3s ease-in-out",
      // FIX: GPU layer only during drag — saves memory when idle
      willChange: is_dragging ? "height" : "auto",
      transform: "translateZ(0)",
    }),
    [sidebar_height, is_dragging],
  );

  const drag_handle_button_style = useMemo(
    () => ({
      width: is_dragging ? "111px" : "35px",
      height: is_dragging ? "17px" : "5px",
      backgroundColor: is_dragging ? "#000" : "#ccc",
      borderRadius: "2em",
      transition: "background-color 0.2s ease-in-out",
    }),
    [is_dragging],
  );

  // ─── Derived JSX ──────────────────────────────────────────────────────────────

  const pc_sidebar = (
    <div style={pc_sidebar_style}>
      <SIDEBAR_CONTENT />
    </div>
  );

  const mobile_sidebar = (
    <div style={mobile_sidebar_style}>
      <div
        style={drag_handle_container_style}
        onMouseDown={handle_mouse_down}
        onTouchStart={handle_touch_start}
      >
        <button style={drag_handle_button_style} />
      </div>
      <div style={sidebar_inner_style}>
        <SIDEBAR_CONTENT />
      </div>
    </div>
  );

  const mobile_sidebar_closed = (
    <div style={mobile_sidebar_closed_style} onClick={activate_bar}>
      <SIDEBAR_MINI />
    </div>
  );

  const modal_cancel_confirmation_content =
    modal_cancel_confirmation?.is_open && (
      <Modal
        isOpen={modal_cancel_confirmation.is_open}
        onClose={() => set_modal_cancel_confirmation(false)}
        modalSize="small"
        title={dict["Confirmation"][language]}
        is_hide="hide"
      >
        <div style={modal_body_style}>
          <p style={modal_text_style}>
            {dict[
              "Are you sure you want to cancel? Your _toolname_ data will be cleared."
            ][language].replace("_toolname_", name_toolbox)}
          </p>
        </div>
        <div style={modal_actions_style}>
          <button
            onClick={() => set_modal_cancel_confirmation(false)}
            className="button_pill"
            style={cancel_no_button_style}
          >
            {dict["No"][language]}
          </button>
          <button
            onClick={handle_cancel}
            className="button_pill"
            style={cancel_yes_button_style}
          >
            {dict["Yes"][language]}
          </button>
        </div>
      </Modal>
    );

  let final_content;
  if (is_mobile_close || is_transitioning_open) {
    final_content = mobile_sidebar_closed;
  } else if (is_mobile) {
    final_content = mobile_sidebar;
  } else {
    final_content = pc_sidebar;
  }

  return (
    <main>
      <TOOLBOX_COMPANION />
      <TOOLBOX_SETTING />
      {final_content}
      {modal_cancel_confirmation_content}
    </main>
  );
};

// ─── Static styles (defined once, outside component) ─────────────────────────

const drag_handle_container_style = {
  width: "100%",
  height: "20px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "row-resize",
  flexShrink: 0,
  // touchAction only on the handle — not the whole sidebar
  // so the map beneath sidebar content stays fully interactive
  touchAction: "none",
};

const sidebar_inner_style = {
  flexGrow: 1,
  overflowY: "hidden",
  paddingRight: "15px",
  paddingLeft: "15px",
  display: "flex",
  justifyContent: "center",
};

const mobile_sidebar_closed_style = {
  zIndex: 3,
  position: "fixed",
  bottom: "45px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "#fff",
  width: "229px",
  height: "90px",
  borderRadius: "45px",
  boxSizing: "border-box",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease-in-out",
  // FIX: simple border instead of double boxShadow
  border: "1px solid rgba(0, 0, 0, 0.10)",
};

const modal_body_style = { marginBottom: "8px" };
const modal_text_style = {
  display: "inline",
  fontWeight: 600,
  fontSize: "14px",
};
const modal_actions_style = {
  display: "flex",
  justifyContent: "end",
  gap: "10px",
};
const cancel_no_button_style = { border: "1px solid black", color: "black" };
const cancel_yes_button_style = { backgroundColor: "#BD0000", color: "white" };

// ─── Redux ────────────────────────────────────────────────────────────────────

const map_state_to_props = (state) => ({
  layer: state.layer,
  properties: state.properties,
  toolbox: state.toolbox,
});

export default connect(map_state_to_props, {
  set_value_properties,
  set_value_layer,
  set_value_insight,
  toolbox_handle_cancel,
  set_value_toolbox,
  toolbox_handle_stop,
  toolbox_handle_back,
})(RIGHT_CONTAINER);
